import { Box } from "@mui/material";
import { ComponentType, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { TOOLBAR_CONTAINER } from "components/toolbars/constants";
import { EnhancedTooltip } from "components/toolbars/tooltips/EnhancedTooltip";
import { useFlyoutRegistration } from "components/toolbars/useFlyoutRegistration";
import { Picker, selectPicker } from "features/render";
import { useCloseOnOutsidePointerDown } from "hooks/useCloseOnOutsidePointerDown";
import SlidersIcon from "media/icons/sliders.svg?react";

import { DefaultIconButton } from "./defaultIconButton";
import { LaserSettings } from "./laserSettings";
import { PointToPointSettings } from "./pointToPointSettings";

/** Maps a picker to the settings overview it exposes. Pickers without settings are omitted. */
const pickerSettings: Partial<Record<Picker, ComponentType>> = {
    [Picker.OutlineLaser]: LaserSettings,
    [Picker.PointLine]: PointToPointSettings,
};

/**
 * Tool settings button shown below the close (X) button. Only rendered when the active tool
 * exposes settings. Clicking it toggles the settings overview; clicking outside the overview
 * or the button closes it.
 */
export function ToolSettings() {
    const { t } = useTranslation();
    const picker = useAppSelector(selectPicker);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const Settings = pickerSettings[picker];

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!Settings) {
            setOpen(false);
        }
    }, [Settings]);

    useCloseOnOutsidePointerDown(open, containerRef, close);
    useFlyoutRegistration(open);

    if (!Settings) {
        return null;
    }

    return (
        <Box ref={containerRef} sx={{ position: "relative", pointerEvents: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    width: `${TOOLBAR_CONTAINER.WIDTH}px`,
                    padding: `${TOOLBAR_CONTAINER.PADDING}px`,
                    backgroundColor: TOOLBAR_CONTAINER.BG,
                    border: TOOLBAR_CONTAINER.BORDER,
                    borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px`,
                    boxShadow: TOOLBAR_CONTAINER.SHADOW,
                }}
            >
                <EnhancedTooltip
                    config={{ toolName: "toolSettings", shortcutKey: "", placement: "left", alwaysVisible: false }}
                >
                    <DefaultIconButton
                        id="tool-settings"
                        active={open}
                        aria-label={t("toolSettings")}
                        onClick={() => setOpen((prev) => !prev)}
                    >
                        <SlidersIcon />
                    </DefaultIconButton>
                </EnhancedTooltip>
            </Box>
            {open && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: "calc(100% + 8px)",
                        zIndex: 1,
                    }}
                >
                    <Settings />
                </Box>
            )}
        </Box>
    );
}
